import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import { ListItemIcon, ListItemText } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { ContactsAnalyticsService } from "@analytics/index";

import ContactsTable from "@components/ContactTable/ContactsTable";
import useContactsTableConfig from "@components/ContactTable/useContactsTableConfig";
import useContactsTableToolbar from "@components/ContactTable/useContactsTableToolbar";
import ContactFilterDrawer from "@components/FilterControls/ContactFilterDrawer";
import TableEmptyState from "@components/Table/TableEmptyState";
import {
  TableToolbar,
  TableToolbarDropdown,
  TableToolbarDropdownMenuItem,
  TableToolbarFilterButton,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import C from "@constants/constants";

import useRemoveContacts from "@hooks/useRemoveContacts";

import { ContactType } from "@interfaces/contact.type";

import ContactService from "@services/contact.service";

import { useAdminStore } from "@store/adminStore";
import { IContactTableControls } from "@store/filters/filter.types";
import { useTableStore } from "@store/filters/filters.store";
import { useUserStore } from "@store/userStore";

interface ITechnologyContactsProperties {
  contacts: ContactType[];
  isLoading?: boolean;
  // TODO: What is this?
  shouldUseTabHeight?: boolean;
  technologyId: string;
  technologyName: string;
}

export default function TechnologyContacts({
  contacts,
  isLoading,
  shouldUseTabHeight,
  technologyId,
  technologyName,
}: ITechnologyContactsProperties) {
  const { isAdminDrawerOpen, setIsAdminDrawerOpen } = useAdminStore();

  const { role } = useUserStore();

  const { filters, pagination, resetTableStore, selection, setTableFilters, setTableSelection, sort } =
    useTableStore("technologyContact");

  const handleRemoveContacts = useRemoveContacts();

  const oldFilters = useRef<IContactTableControls>({
    filters,
    pagination,
    selection,
    sort,
  });

  useEffect(() => {
    if (
      !_.isEqual(
        {
          filters,
          pagination,
          sort,
        },
        oldFilters.current,
      )
    ) {
      ContactsAnalyticsService.trackContactFilter(
        {
          filters,
          pagination,
          selection,
          sort,
        },
        oldFilters.current,
        "technology",
      );
      oldFilters.current = {
        filters,
        pagination,
        selection,
        sort,
      };
    }
  }, [filters, sort, pagination]);

  const filteredContacts = useMemo(() => {
    //This is needed because if we are viewing technology contacts we are using FE filtering, otherwise we use backend
    const filteredContactIndexes = ContactService.filterContacts(contacts, {
      hasAttendedConference: filters.hasAttendedConference ?? false,
      hasVerifiedEmail: filters.hasVerifiedEmail ?? false,
      jobTitles: filters.jobTitles ?? [],
      keywords: filters.keywords ?? [],
      locations: filters.locations ?? [],
    });
    return _.sortBy(_.at(contacts, filteredContactIndexes), (contact) => contact.companyName);
  }, [contacts, filters]);

  const removeContact = useCallback(
    (contactId: string) => {
      const contactIds = [contactId];
      void handleRemoveContacts({
        contactIds,
        technologyId,
        technologyName,
      });
    },
    [handleRemoveContacts, technologyId, technologyName],
  );

  const contactTableRowMenuItems = useMemo(
    () => [
      {
        func: removeContact,
        label: "Remove from Technology",
      },
    ],
    [removeContact],
  );

  const {
    handleAddContactsToTechnology,
    handleExportContacts,
    handleRemoveContacts: handleRemoveMultipleContacts,
    isContactResultsPage,
    isFilterOpen,
    isSmartSearch,
    toggleFilters,
  } = useContactsTableToolbar({
    contacts: filteredContacts,
    selectedIds: selection ?? [],
    technologyName,
    workflow: "technology",
  });

  const { columns } = useContactsTableConfig({ rowMenuItems: contactTableRowMenuItems, workflow: "technology" });

  const activeFilters = useMemo(() => {
    return Object.entries(filters).reduce((count, [_, value]) => {
      if (Array.isArray(value)) {
        return count + value.length;
      }
      return count + (value ? 1 : 0);
    }, 0);
  }, [filters]);

  return contacts.length === 0 && !isLoading ? (
    <TableEmptyState
      heading="Start Connecting"
      resourceLink={C.LOOM_CONTACTS}
      state="technology contacts"
      supportingText="Add contacts from your searches to this Technology."
    />
  ) : (
    <>
      <ContactFilterDrawer
        filters={filters}
        isOpen={isFilterOpen}
        resetFilters={() => resetTableStore({ filters: true })}
        setFilters={setTableFilters}
        setOpen={toggleFilters}
      />
      <TableToolbar>
        <TableToolbarLeft>
          <TableToolbarTitle
            resourceLink={C.LOOM_CONTACTS}
            tableSubtitle="Select contacts to create a Campaign or download to CSV. All contact emails will appear in your CSV."
            tableTitle="Contacts"
          />
        </TableToolbarLeft>
        <TableToolbarRight>
          <TableToolbarFilterButton
            activeFilters={activeFilters}
            toggleFilterVisibility={toggleFilters}
            tooltipText="Filter contacts from company."
          />
          <TableToolbarDropdown
            actionsLabel={isContactResultsPage || isSmartSearch ? "Save Contacts" : "Actions"}
            selectedIds={selection ?? []}
            tooltipText="Save selected contacts to Portfolio or download to CSV."
          >
            <TableToolbarDropdownMenuItem onClick={handleAddContactsToTechnology}>
              <ListItemIcon>
                <LightbulbRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Save To Portfolio</ListItemText>
            </TableToolbarDropdownMenuItem>
            <TableToolbarDropdownMenuItem onClick={handleRemoveMultipleContacts}>
              <ListItemIcon>
                <DeleteForeverRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Remove Contacts</ListItemText>
            </TableToolbarDropdownMenuItem>
            <TableToolbarDropdownMenuItem onClick={handleExportContacts}>
              <ListItemIcon>
                <DownloadRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export Contact Data</ListItemText>
            </TableToolbarDropdownMenuItem>
          </TableToolbarDropdown>
          {role === "Admin" && (
            <Button
              color={isAdminDrawerOpen ? "primary" : "secondary"}
              onClick={() => setIsAdminDrawerOpen(true)}
              startIcon={<AdminPanelSettingsRoundedIcon />}
              sx={{ borderRadius: "8px", height: "40px" }}
              variant="contained"
            >
              <Typography fontWeight={600} variant="body2">
                Admin Drawer
              </Typography>
            </Button>
          )}
        </TableToolbarRight>
      </TableToolbar>
      <ContactsTable
        columns={columns}
        contacts={filteredContacts}
        isLoading={isLoading}
        paginationMode="client"
        rowMenuItems={contactTableRowMenuItems}
        selectedIds={selection ?? []}
        setSelectedIds={setTableSelection}
        shouldUseTabHeight={shouldUseTabHeight}
        total={filteredContacts.length}
      />
    </>
  );
}
